import {
  CheckOutlined,
  CloseOutlined,
  PaperClipOutlined,
  PlusOutlined
} from "@ant-design/icons"
import { App, Button, DatePicker, Flex, Form, Select, Tag, Tooltip } from "antd"
import type { BaseOptionType } from "antd/lib/select"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useParams } from "react-router"

import { rowCommitKeyDown } from "../../../../../../../components/EntityTable/editable-row-cells.ts"
import { useRowEscapeCancel } from "../../../../../../../components/EntityTable/use-row-escape-cancel.ts"
import { empty } from "../../../../../../../constants/placeholders"
import { MODAL_CONFIRM_CONFIG } from "../../../../../../../components/Forms/FormChangedWatcher/FormChangedConfirm.tsx"
import { FormChangedWatcher } from "../../../../../../../components/Forms/FormChangedWatcher/FormChangedWatcher.tsx"
import InputNumber from "../../../../../../../components/Input/InputNumber.tsx"
import { useDateFormat } from "../../../../../../../hooks/use-date-format.ts"
import { DATE_FORMAT } from "../../../../../../../utils/date/date.ts"
import { dateAutoFill } from "../../../../../../../utils/form/date-auto-fill.tsx"
import { validateFormWithScroll } from "../../../../../../../utils/form/validate-form-with-scroll.tsx"
import { isFormChanged } from "../../../../../../../utils/object/is-form-changed.ts"
import { pickableOptions } from "../../../../../../../utils/select/managed-list-options.ts"
import type { FinancialTransactionItem } from "../../../../../types/project-finance.types.tsx"
import type { ProjectInvoice } from "../../../../../types/project-invoices.types.tsx"
import {
  ENTRY_TYPE_LABELS,
  ENTRY_TYPE_OPTIONS,
  ENTRY_TYPES,
  FINANCE_ITEM_STATUSES,
  FINANCE_ITEM_STATUSES_MAP,
  FINANCE_ITEM_TYPES,
  isMaterialLine,
  LINE_KINDS,
  type LineKind
} from "../../../../constants/finance-item-constants.ts"
import { ProjectViewFinanceCellDueDate } from "../../../ProjectViewFinanceCellDueDate.tsx"
import { normalize } from "./buildStakeholderOptions.ts"
import { MaterialSelect } from "./MaterialSelect.tsx"
import {
  applyMaterialEdit,
  materialUnitFigure,
  type MaterialLineDraft,
  type MaterialLineEdit,
  type MaterialUnitBasis
} from "./material-line-derivation.ts"
import { ProjectViewFinanceNewRowMilestones } from "./ProjectViewFinanceNewRowMilestones.tsx"
import { ProjectViewFinancePersonSelect } from "./ProjectViewFinancePersonSelect.tsx"
import { QuickAddInvoiceControl } from "../../../../../../Invoices/components/QuickAddInvoiceControl.tsx"
import { ViewInvoiceButton } from "../../../../../../Invoices/components/ViewInvoiceButton.tsx"
import { invoiceFormFieldErrors } from "../../../../../../Invoices/helpers/handle-invoice-api-error.ts"
import {
  buildItemTypeOptions,
  buildResponsibleTypeOptions,
  isExpenseAllowed,
  isPayableAllowed,
  isReceivableAllowed,
  materialResponsibleType,
  PAYABLE_DISABLED_REASON,
  RECEIVABLE_DISABLED_REASON,
  responsibleTypeDisabledReason,
  sourceTargetPatch,
  sourceTargetValue,
  type SourceTargetValue
} from "./responsible-type-options.ts"
import { statusOnCreate } from "./status-on-create.ts"
import { buildStatusOptions } from "./status-options.ts"
import { RESPONSIBLE_TYPES } from "../../../../constants/responsible-type-constants.ts"
import { ResolvedSelect } from "../../../../../../../components/Select/ResolvedSelect.tsx"
import { TextArea } from "../../../../../../../components/Input/TextArea.tsx"
import { TextInput } from "../../../../../../../components/Input/TextInput.tsx"

type MaterialFields = Omit<MaterialLineDraft, "value">

const EMPTY_MATERIAL_FIELDS: MaterialFields = {
  supplier: null,
  material: null,
  material_name: null,
  material_supplier_name: null,
  material_unit_price: null,
  material_unit_cost: null,
  material_quantity: null
}

interface ProjectViewFinanceNewRowProps {
  record: FinancialTransactionItem
  columns: {
    key?: React.Key
    width?: number | string
    fixed?: boolean | "left" | "right"
  }[]
  options: Record<string, BaseOptionType[]>
  mode?: "create" | "edit"
  onSelectRow: (
    record: FinancialTransactionItem,
    action: string
  ) => Promise<void> | void
  invoiceCreateAllowed?: boolean
  invoiceSelfAssigned?: boolean
  userId?: string
}

export const ProjectViewFinanceNewRow = ({
  record,
  columns,
  onSelectRow,
  options,
  mode = "create",
  invoiceCreateAllowed,
  invoiceSelfAssigned,
  userId,
  ...rest
}: ProjectViewFinanceNewRowProps) => {
  const { getDateValueProps, normalizeDate, userFormat } = useDateFormat()
  const [saving, setSaving] = useState(false)
  const [form] = Form.useForm<FinancialTransactionItem>()
  const { modal } = App.useApp()
  const { t } = useTranslation()

  const projectId = useParams()?.id as string | undefined
  const [extraInvoiceOptions, setExtraInvoiceOptions] = useState<
    BaseOptionType[]
  >([])

  const isCreate = mode === "create"
  const projectTypesOptions = options?.["project_type"]

  const itemType = Form.useWatch("item_type", form)
  const responsibleType = Form.useWatch("responsible_type", form)
  const partyName = Form.useWatch("custom_subcontractor_name", form)

  // The material snapshot lives beside the form rather than in it: its cells
  // are user-hideable columns, and a hidden column must not silently drop the
  // figures the line was saved with.
  const [lineKind, setLineKind] = useState<LineKind | null>(
    record?.line_kind ?? null
  )
  const [materialFields, setMaterialFields] = useState<MaterialFields>(() => ({
    supplier: record?.supplier ?? null,
    material: record?.material ?? null,
    material_name: record?.material_name ?? null,
    material_supplier_name: record?.material_supplier_name ?? null,
    material_unit_price: record?.material_unit_price ?? null,
    material_unit_cost: record?.material_unit_cost ?? null,
    material_quantity: record?.material_quantity ?? null
  }))
  const isMaterial = isMaterialLine(lineKind)
  const isMaterialPurchase =
    isMaterial && itemType === FINANCE_ITEM_TYPES.PAYABLE
  const unitBasis: MaterialUnitBasis = isMaterialPurchase ? "cost" : "sale"

  const expenseAllowed = isExpenseAllowed(itemType, record?.is_planned)
  const createStatus = statusOnCreate(responsibleType, record?.is_planned)
  // Material is offered on both sides, so the customer-versus-payable mirror
  // rules — which exist to stop a save the API would refuse — never apply to it.
  const receivableAllowed = isMaterial || isReceivableAllowed(responsibleType)
  const payableAllowed = isMaterial || isPayableAllowed(responsibleType)
  const responsibleTypeOptions = useMemo(
    () =>
      buildResponsibleTypeOptions(
        options?.["responsible_type"],
        expenseAllowed,
        itemType,
        { label: t("finance.material.sourceOption") }
      ),
    [options, expenseAllowed, itemType, t]
  )
  const itemTypeOptions = useMemo(
    () =>
      buildItemTypeOptions(
        options?.["item_type"],
        receivableAllowed,
        payableAllowed
      ),
    [options, receivableAllowed, payableAllowed]
  )
  const statusOptions = useMemo(
    () => buildStatusOptions(options?.["status"], record?.partial_finances),
    [options, record?.partial_finances]
  )

  const entryType = Form.useWatch("entry_type", form)
  const pickedInvoice = Form.useWatch("invoice", form)
  // Keep a legacy entry_type (e.g. invoice/refund on an existing row) selectable
  // with its proper label, even though the picker is reduced to the active types.
  const entryTypeOptions = useMemo(() => {
    const base = options?.["entry_type"]?.length
      ? options["entry_type"]
      : ENTRY_TYPE_OPTIONS
    if (entryType && !base.some((option) => option.value === entryType)) {
      return [
        { value: entryType, label: ENTRY_TYPE_LABELS[entryType] ?? entryType },
        ...base
      ]
    }
    return base
  }, [options, entryType])

  // EXPENSE only saves on an actual payable line; if the line turns receivable
  // or planned, drop the now-invalid Source/Target instead of letting the user
  // submit into a server rejection.
  useEffect(() => {
    if (responsibleType === RESPONSIBLE_TYPES.EXPENSE && !expenseAllowed) {
      form.setFieldValue("responsible_type", null)
    }
  }, [expenseAllowed, responsibleType, form])

  // A Receivable is customer-only; if the contact becomes a non-customer, drop
  // the now-invalid type so it can't be saved against a non-customer.
  useEffect(() => {
    if (itemType === FINANCE_ITEM_TYPES.RECEIVABLE && !receivableAllowed) {
      form.setFieldValue("item_type", null)
    }
  }, [receivableAllowed, itemType, form])

  // The mirror: a customer can't be a payable; if the contact becomes a
  // customer, drop the now-invalid type.
  useEffect(() => {
    if (itemType === FINANCE_ITEM_TYPES.PAYABLE && !payableAllowed) {
      form.setFieldValue("item_type", null)
    }
  }, [payableAllowed, itemType, form])

  const editMaterial = useCallback(
    (edit: MaterialLineEdit) => {
      const currentValue = form.getFieldValue("value")
      const { value, ...fields } = applyMaterialEdit(
        {
          ...materialFields,
          value: currentValue == null ? null : String(currentValue)
        },
        edit,
        unitBasis
      )
      if (value !== undefined) form.setFieldValue("value", value)
      if (Object.keys(fields).length) {
        setMaterialFields((current) => ({ ...current, ...fields }))
      }
    },
    [form, materialFields, unitBasis]
  )

  const supplierOptions = useMemo(() => options?.["suppliers"] ?? [], [options])

  // Buying material, the name field IS the supplier list, so the price list
  // behind the material is whichever supplier that name resolves to. A name
  // typed freehand resolves to none, and there is nothing to choose from.
  const namedSupplier = useMemo(() => {
    const name = String(partyName ?? "").trim()
    if (!name) return { id: null, name: null }
    const match = supplierOptions.find(
      (option) => normalize(String(option.label ?? "")) === normalize(name)
    )
    return match
      ? { id: String(match.value), name: String(match.label) }
      : { id: null, name }
  }, [partyName, supplierOptions])

  useEffect(() => {
    if (!isMaterialPurchase) return
    if (
      materialFields.supplier === namedSupplier.id &&
      materialFields.material_supplier_name === namedSupplier.name
    )
      return
    editMaterial({
      kind: "change-supplier",
      supplier: namedSupplier.id,
      supplierName: namedSupplier.name
    })
  }, [
    isMaterialPurchase,
    namedSupplier.id,
    namedSupplier.name,
    materialFields.supplier,
    materialFields.material_supplier_name,
    editMaterial
  ])

  // The two flows read a different unit figure, so the value a line was carrying
  // stops being its own the moment it changes sides. Skipped on the first run:
  // a saved line opens on the basis it was saved under and must not be rewritten
  // just for being looked at.
  const settledBasis = useRef<MaterialUnitBasis | null>(null)
  useEffect(() => {
    if (!isMaterial || itemType == null) return
    const previous = settledBasis.current
    settledBasis.current = unitBasis
    if (previous === null || previous === unitBasis) return
    editMaterial({ kind: "change-basis" })
  }, [isMaterial, itemType, unitBasis, editMaterial])

  // Material is one word for two flows; switching the transaction type moves
  // the line from the customer to the supplier without ever unpicking it.
  useEffect(() => {
    // `itemType` is undefined until the watch reads the initial values, and
    // answering that with a party would move a saved line to the wrong one.
    if (!isMaterial || itemType == null) return
    const expected = materialResponsibleType(itemType)
    if (responsibleType !== expected) {
      form.setFieldValue("responsible_type", expected)
    }
  }, [isMaterial, itemType, responsibleType, form])

  const handleSourceTargetChange = (picked: SourceTargetValue | undefined) => {
    const patch = sourceTargetPatch(picked, itemType)
    form.setFieldValue("responsible_type", patch.responsible_type)
    setLineKind(patch.line_kind)
    // A line that stops carrying a material must not keep one. The value is
    // money the user entered and stays where it is.
    if (patch.line_kind !== LINE_KINDS.MATERIAL) {
      setMaterialFields(EMPTY_MATERIAL_FIELDS)
    }
  }

  const hasFreeTypedSupplier =
    isMaterialPurchase &&
    !materialFields.supplier &&
    !!materialFields.material_supplier_name
  const unitFigure = materialUnitFigure(materialFields, unitBasis)

  const projectTypeMilestonesMap = useMemo((): Map<
    string,
    BaseOptionType[]
  > => {
    return new Map(
      projectTypesOptions?.map((item) => [item?.value, item?.milestones]) ?? []
    )
  }, [projectTypesOptions])

  const handleSave = async () => {
    let validated: FinancialTransactionItem
    try {
      validated = await validateFormWithScroll(form)
    } catch {
      return
    }

    const {
      id,
      partial_finances: _partialFinances,
      planned_value: _plannedValue,
      ...initialData
    } = record
    // A column hidden in Table settings renders no editor, so its field is not
    // in the validated values; the form store still holds the line's own value
    // and anything a visible editor wrote into it.
    const {
      id: _storedId,
      partial_finances: _storedPartials,
      planned_value: _storedPlanned,
      ...stored
    } = form.getFieldsValue(true) as FinancialTransactionItem
    const { date, due_date, ...values } = { ...stored, ...validated }

    const action = isCreate ? "create" : "update"

    setSaving(true)

    try {
      await onSelectRow(
        {
          ...initialData,
          ...values,
          ...materialFields,
          line_kind: lineKind,
          subcontractor: values?.subcontractor || null,
          assignee: values?.assignee || null,
          custom_subcontractor_name: values?.custom_subcontractor_name || null,
          date: normalizeDate(date),
          due_date: normalizeDate(due_date)
        } as FinancialTransactionItem,
        action
      )
    } catch (rejection) {
      // A line pointed at a draft or voided invoice is refused on `invoice`;
      // the reason belongs on that cell rather than only in the toast.
      const invoiceError = invoiceFormFieldErrors(
        (rejection as { data?: unknown } | undefined)?.data ?? rejection
      ).find((entry) => entry.name === "invoice")
      if (invoiceError) {
        form.setFields([{ name: "invoice", errors: invoiceError.errors }])
      }
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => {
    const action = isCreate ? "cancel_create" : "cancel"

    // The material snapshot is not in the form, so the guard has to be told
    // about it or picking a material and cancelling would discard it silently.
    const isChanged = isFormChanged<FinancialTransactionItem>(
      { ...form.getFieldsValue(), ...materialFields, line_kind: lineKind },
      record
    )

    if (isChanged) {
      modal.confirm({
        ...MODAL_CONFIRM_CONFIG.cancelChanges,
        onOk() {
          onSelectRow(record, action)
        }
      })
    } else {
      onSelectRow(record, action)
    }
  }

  const handleInvoiceCreated = (invoice: ProjectInvoice) => {
    setExtraInvoiceOptions((prev) => [
      { value: invoice.id, label: invoice.invoice_number },
      ...prev.filter((option) => option.value !== invoice.id)
    ])
    form.setFieldValue("invoice", invoice.id)
  }

  const rowClassName = isCreate ? "bg-blue-50" : ""

  const fixedRightOffsets = useMemo(() => {
    const offsets: Record<string, number> = {}
    let offset = 0
    for (let i = columns.length - 1; i >= 0; i--) {
      const column = columns[i]
      if (column.fixed === "right") {
        offsets[String(column.key)] = offset
        offset += Number(column.width) || 0
      }
    }
    return offsets
  }, [columns])

  const fixedLeftOffsets = useMemo(() => {
    const offsets: Record<string, number> = {}
    let offset = 0
    for (const column of columns) {
      if (column.fixed === "left") {
        offsets[String(column.key)] = offset
        offset += Number(column.width) || 0
      }
    }
    return offsets
  }, [columns])

  const lastFixedLeftKey = useMemo(
    () => [...columns].reverse().find((column) => column.fixed === "left")?.key,
    [columns]
  )

  const fixedLeftCellProps = (key: string) =>
    key in fixedLeftOffsets
      ? {
          className: [
            "sticky ant-table-cell ant-table-cell-fix-left",
            String(key) === String(lastFixedLeftKey) &&
              "ant-table-cell-fix-left-last",
            rowClassName
          ]
            .filter(Boolean)
            .join(" "),
          style: { left: fixedLeftOffsets[key] }
        }
      : { className: "ant-table-cell" }

  const firstFixedRightKey = useMemo(
    () => columns.find((column) => column.fixed === "right")?.key,
    [columns]
  )

  const fixedRightCellProps = (key: string, extraClassName = "") => {
    if (!(key in fixedRightOffsets)) {
      return {
        className: ["ant-table-cell", extraClassName, rowClassName]
          .filter(Boolean)
          .join(" ")
      }
    }

    return {
      className: [
        "sticky ant-table-cell ant-table-cell-fix-right",
        String(key) === String(firstFixedRightKey) &&
          "ant-table-cell-fix-right-first",
        extraClassName,
        rowClassName
      ]
        .filter(Boolean)
        .join(" "),
      style: { right: fixedRightOffsets[key] }
    }
  }

  const cellByKey: Record<string, React.ReactNode> = {
    date: (
      <td className="ant-table-cell">
        <Form.Item<FinancialTransactionItem>
          name="date"
          getValueProps={getDateValueProps}
          noStyle
          rules={[{ required: true, message: "Required" }]}
        >
          <DatePicker
            className="w-full"
            format={userFormat ?? DATE_FORMAT}
            onChange={dateAutoFill<FinancialTransactionItem>({
              form,
              endDatePath: ["due_date"]
            })}
          />
        </Form.Item>
      </td>
    ),
    name: (
      <td {...fixedLeftCellProps("name")}>
        <Form.Item<FinancialTransactionItem>
          name="name"
          noStyle
          rules={[
            { required: true, message: "Enter name" },
            {
              max: 255,
              message: "Must be at least 255 characters"
            }
          ]}
        >
          <TextArea autoSize placeholder="Enter name" autoFocus={isCreate} />
        </Form.Item>
      </td>
    ),
    item_type_display: (
      <td className="ant-table-cell">
        <Form.Item<FinancialTransactionItem>
          name="item_type"
          noStyle
          rules={[{ required: true, message: "Required" }]}
        >
          <Select
            className="w-full"
            placeholder="Select type"
            options={itemTypeOptions}
            optionRender={(option) =>
              option.data?.disabled ? (
                <Tooltip
                  title={
                    option.value === FINANCE_ITEM_TYPES.PAYABLE
                      ? PAYABLE_DISABLED_REASON
                      : RECEIVABLE_DISABLED_REASON
                  }
                  placement="left"
                >
                  <span className="block">{option.label}</span>
                </Tooltip>
              ) : (
                option.label
              )
            }
          />
        </Form.Item>
      </td>
    ),
    entry_type: (
      <td className="ant-table-cell">
        <Form.Item<FinancialTransactionItem>
          name="entry_type"
          noStyle
          rules={[{ required: true, message: "Required" }]}
        >
          <Select
            className="w-full"
            placeholder="Select entry type"
            options={entryTypeOptions}
          />
        </Form.Item>
      </td>
    ),
    due_date: (
      <ProjectViewFinanceCellDueDate<FinancialTransactionItem> form={form} />
    ),
    comment: (
      <td className="ant-table-cell">
        <Form.Item<FinancialTransactionItem>
          name="comment"
          noStyle
          rules={[
            {
              whitespace: true,
              max: 250,
              message: "Must be at least 250 characters"
            }
          ]}
        >
          <TextArea rows={2} autoSize placeholder="Enter comment" />
        </Form.Item>
      </td>
    ),
    value: (
      <td {...fixedLeftCellProps("value")}>
        <Form.Item<FinancialTransactionItem>
          name="value"
          noStyle
          rules={[{ required: false, message: "Required" }]}
        >
          <InputNumber
            min={0}
            max={999999999.99}
            precision={2}
            className="w-full"
            prefix="$"
            placeholder="0.00"
          />
        </Form.Item>
      </td>
    ),
    responsible_type_display: (
      <td className="ant-table-cell">
        {/* Registered on `responsible_type` so the field reaches the payload,
            but reading and writing both of the fields "Material" stands for.
            The read goes through `getValueProps`, since a value the form injects
            would otherwise win over one passed to the control and a material
            line would read back as the party underneath it. */}
        <Form.Item<FinancialTransactionItem>
          name="responsible_type"
          noStyle
          getValueProps={(value: number | null) => ({
            value: sourceTargetValue(value, lineKind)
          })}
        >
          <Select
            allowClear
            className="w-full"
            placeholder="Select type"
            options={responsibleTypeOptions}
            onChange={handleSourceTargetChange}
            optionRender={(option) =>
              option.data?.disabled ? (
                <Tooltip
                  title={responsibleTypeDisabledReason(
                    option.value,
                    itemType,
                    expenseAllowed
                  )}
                  placement="left"
                >
                  <span className="block">{option.label}</span>
                </Tooltip>
              ) : (
                option.label
              )
            }
          />
        </Form.Item>
      </td>
    ),
    custom_subcontractor_name: (
      <td className="ant-table-cell">
        <ProjectViewFinancePersonSelect
          form={form}
          subcontractors={options?.["subcontractors"]}
          projectSubcontractors={options?.["project_subcontractors"]}
          teamMembers={options?.["team_members"]}
          suppliers={options?.["suppliers"]}
          hasInitialValue={Boolean(
            record?.custom_subcontractor_name ||
            record?.subcontractor ||
            record?.assignee
          )}
        />
      </td>
    ),
    material_name: (
      <td className="ant-table-cell">
        {isMaterial ? (
          <Flex vertical gap={4}>
            {/* Buying material, the Name field already is the supplier. */}
            {!isMaterialPurchase && (
              <ResolvedSelect
                className="w-full"
                placeholder={t("finance.material.supplierPlaceholder")}
                aria-label={t("finance.material.supplierPlaceholder")}
                options={supplierOptions}
                value={materialFields.supplier}
                showSearch
                allowClear
                filterOption={(input, option) =>
                  String(option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                onChange={(supplier?: string) =>
                  editMaterial({
                    kind: "change-supplier",
                    supplier: supplier ?? null,
                    supplierName:
                      String(
                        supplierOptions.find(
                          (option) => option.value === supplier
                        )?.label ?? ""
                      ) || null
                  })
                }
              />
            )}
            {hasFreeTypedSupplier ? (
              // No supplier record, so no price list to choose from: the
              // material is named by hand and so is the value.
              <TextInput
                placeholder={t("finance.material.namePlaceholder")}
                aria-label={t("finance.columns.material")}
                value={materialFields.material_name ?? ""}
                onChange={(event) =>
                  editMaterial({
                    kind: "type-material-name",
                    name: event.target.value
                  })
                }
              />
            ) : (
              <MaterialSelect
                supplier={materialFields.supplier}
                basis={unitBasis}
                material={materialFields.material}
                materialName={materialFields.material_name}
                onPick={(material) =>
                  editMaterial({ kind: "pick-material", material })
                }
                onType={(name) =>
                  editMaterial({ kind: "type-material-name", name })
                }
              />
            )}
          </Flex>
        ) : null}
      </td>
    ),
    material_quantity: (
      <td className="ant-table-cell">
        {isMaterial ? (
          <InputNumber
            min={0}
            max={999999999.999}
            precision={3}
            className="w-full"
            placeholder="0.000"
            aria-label={t("finance.columns.materialQuantity")}
            value={
              materialFields.material_quantity == null
                ? null
                : Number(materialFields.material_quantity)
            }
            onChange={(quantity) =>
              editMaterial({
                kind: "change-quantity",
                quantity: quantity == null ? null : String(quantity)
              })
            }
          />
        ) : null}
      </td>
    ),
    material_unit_price: (
      <td className="ant-table-cell">
        {isMaterial ? (
          // Read-only on purpose: the catalogue owns the figure, and Value is
          // the lever for a one-off one. Which figure it is follows the flow —
          // the sale price selling, the cost buying.
          <InputNumber
            disabled
            precision={2}
            className="w-full"
            prefix="$"
            placeholder={empty}
            aria-label={t("finance.columns.materialUnitPrice")}
            value={unitFigure == null ? null : Number(unitFigure)}
          />
        ) : null}
      </td>
    ),
    project_type_display: (
      <td className="ant-table-cell">
        <Form.Item<FinancialTransactionItem>
          name="project_type"
          noStyle
          normalize={(value: string) => value || null}
          getValueFromEvent={(
            value: FinancialTransactionItem["project_type"]
          ) => {
            form.setFieldValue("project_phase", null)
            return value
          }}
        >
          <Select
            className="w-full"
            placeholder="Select type"
            options={projectTypesOptions}
            showSearch
            allowClear
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input?.toLowerCase())
            }
          />
        </Form.Item>
      </td>
    ),
    project_phase_name: (
      <td className="ant-table-cell">
        <ProjectViewFinanceNewRowMilestones
          form={form}
          options={options}
          projectTypeMilestonesMap={projectTypeMilestonesMap}
          currentMilestoneId={record?.project_phase}
          currentMilestoneName={record?.project_phase_name}
        />
      </td>
    ),
    invoice_display: (
      <td className="ant-table-cell">
        <Flex gap={4} align="center" className="w-full">
          <div className="min-w-0 flex-1">
            <Form.Item<FinancialTransactionItem>
              name="invoice"
              noStyle
              normalize={(value: string) => value || null}
            >
              <Select
                className="w-full"
                placeholder="Select invoice"
                options={[
                  ...(options?.["invoices"] ?? []),
                  ...extraInvoiceOptions
                ]}
                showSearch
                allowClear
                filterOption={(input, option) =>
                  (option?.label ?? "")
                    .toLowerCase()
                    .includes(input?.toLowerCase())
                }
              />
            </Form.Item>
          </div>
          {invoiceCreateAllowed && projectId && (
            <QuickAddInvoiceControl
              projectId={projectId}
              userId={userId}
              selfAssigned={invoiceSelfAssigned}
              onCreated={handleInvoiceCreated}
              tooltip={t("finance.quickAddInvoice.tooltip")}
            >
              <Button
                icon={<PlusOutlined />}
                aria-label={t("finance.quickAddInvoice.tooltip")}
              />
            </QuickAddInvoiceControl>
          )}
          <ViewInvoiceButton invoiceId={pickedInvoice} />
        </Flex>
      </td>
    ),
    payment_method_display: (
      <td className="ant-table-cell">
        <Form.Item<FinancialTransactionItem>
          name="payment_method"
          noStyle
          normalize={(v) => v || null}
        >
          <ResolvedSelect
            allowClear
            className={"w-full"}
            placeholder="Select method"
            options={pickableOptions(options?.["payment_method"])}
          />
        </Form.Item>
      </td>
    ),
    payment_details: (
      <td className="ant-table-cell">
        <Form.Item<FinancialTransactionItem>
          name="payment_details"
          noStyle
          rules={[
            {
              max: 100,
              message: "Must be at least 100 characters"
            }
          ]}
        >
          <TextArea autoSize placeholder="Enter details" />
        </Form.Item>
      </td>
    ),
    payments: <td {...fixedRightCellProps("payments")} />,
    status: (
      <td {...fixedRightCellProps("status")}>
        {isCreate ? (
          // The backend assigns the status when none is sent. There is nothing
          // to pick, so we show a read-only badge of what it will assign and
          // omit `status` from the payload.
          <Tag
            style={FINANCE_ITEM_STATUSES_MAP[createStatus].style}
            className="m-0"
          >
            {FINANCE_ITEM_STATUSES_MAP[createStatus].label}
          </Tag>
        ) : (
          <Form.Item<FinancialTransactionItem>
            name="status"
            noStyle
            rules={[{ required: true, message: "Required" }]}
          >
            <Select
              allowClear
              className="w-full"
              placeholder="Select status"
              options={statusOptions}
            />
          </Form.Item>
        )}
      </td>
    ),
    attachments: (
      <td {...fixedRightCellProps("attachments", "text-center")}>
        <Tooltip title="Only available in view mode">
          <Button disabled icon={<PaperClipOutlined />} />
        </Tooltip>
      </td>
    ),
    actions: (
      <td {...fixedRightCellProps("actions")}>
        <Flex gap="small" justify="center">
          <Button
            variant="outlined"
            color="danger"
            onClick={handleCancel}
            icon={<CloseOutlined />}
          />
          <Button
            loading={saving}
            variant="outlined"
            color="green"
            onClick={handleSave}
            icon={isCreate ? <PlusOutlined /> : <CheckOutlined />}
          />
        </Flex>
      </td>
    )
  }

  useRowEscapeCancel(handleCancel, saving)

  const onKeyDownRow = rowCommitKeyDown(() => void handleSave(), {
    busy: saving,
    cancel: handleCancel
  })

  return (
    <Form<FinancialTransactionItem>
      form={form}
      component={false}
      initialValues={{
        ...record,
        entry_type: record?.entry_type ?? ENTRY_TYPES.PAYMENT
      }}
      disabled={saving}
    >
      <FormChangedWatcher<FinancialTransactionItem>
        form={form}
        saving={saving}
      />

      <tr {...rest} onKeyDown={onKeyDownRow} className={rowClassName}>
        {columns.map((column) => (
          <React.Fragment key={String(column.key)}>
            {cellByKey[String(column.key)] ?? <td className="ant-table-cell" />}
          </React.Fragment>
        ))}
      </tr>
    </Form>
  )
}
